
import { semanticSearch } from '@/lib/semanticSearch';
import { supabase } from '@/integrations/supabase/client';

/**
 * Normalizes scientific terms and units to standardized forms
 * @param query The query string to normalize
 * @returns Normalized query string
 */
function normalizeScientificTerms(text: string): string {
  return text
    .toLowerCase()
    // Normalize units
    .replace(/μg\/l|ug\/l|micrograms per liter/gi, 'ug/l')
    .replace(/mg\/l|milligrams per liter/gi, 'mg/l')
    // Normalize chemical elements and compounds
    .replace(/\bcadmium\b/gi, 'cd')
    .replace(/\bzinc\b/gi, 'zn')
    .replace(/\bcopper\b/gi, 'cu')
    .replace(/\blead\b/gi, 'pb')
    .replace(/\bmercury\b/gi, 'hg')
    // Normalize species names
    .replace(/\banodonta\s+grandis\s+grandis\b/gi, 'anodonta grandis grandis')
    .replace(/\banodonta\s+grandis\b/gi, 'anodonta grandis')
    // Normalize biological terms
    .replace(/\bmetallothionein\b/gi, 'mt')
    .replace(/\bgill\s+tissue\b/gi, 'gill')
    .replace(/\bmantle\s+tissue\b/gi, 'mantle');
}

export function useKnowledgeBaseSearch() {
  const findKnowledgeBaseInContent = async (content: string): Promise<{kbId: string | null, kbName: string | null}> => {
    let kbId: string | null = null;
    let kbName: string | null = null;
    
    // Enhanced KB command detection - be more lenient with formatting
    // Match both /kb ID and /kb:ID formats
    const kbCommandMatch = content.match(/\/kb\s*:?\s*([a-zA-Z0-9-]+)/);
    if (kbCommandMatch && kbCommandMatch[1]) {
      kbId = kbCommandMatch[1];
      console.log(`Found knowledge base ID in command: ${kbId}`);
      
      // Fetch KB name for better context
      try {
        const { data } = await supabase
          .from('knowledge_bases')
          .select('name')
          .eq('id', kbId)
          .single();
          
        if (data) {
          kbName = data.name;
          console.log(`Retrieved KB name: ${kbName}`);
        }
      } catch (err) {
        console.error("Failed to fetch knowledge base name:", err);
      }
      
      return { kbId, kbName };
    } 
    
    // Check for hashtag KB reference
    if (content.includes('#')) {
      const hashtagMatch = content.match(/#([a-zA-Z0-9_-]+)/);
      if (hashtagMatch && hashtagMatch[1]) {
        const tagName = hashtagMatch[1];
        console.log(`Found hashtag KB reference: #${tagName}`);
        
        const uuidRegex = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i;
        if (uuidRegex.test(tagName)) {
          kbId = tagName;
        } else {
          try {
            const { data: knowledgeBases } = await supabase.from('knowledge_bases').select('id, name');
            const matchedKB = knowledgeBases?.find(kb => 
              kb.name.toLowerCase() === tagName.toLowerCase()
            );
            
            if (matchedKB) {
              kbId = matchedKB.id;
              kbName = matchedKB.name;
              console.log(`Found KB by name: ${kbName} (${kbId})`);
            }
          } catch (err) {
            console.error("Failed to fetch knowledge bases by name:", err);
          }
        }
      }
    }
    
    return { kbId, kbName };
  };

  const performKnowledgeBaseSearch = async (
    kbId: string, 
    content: string, 
    addSystemMessage: (content: string) => Promise<void>
  ) => {
    try {
      const { data: kb } = await supabase
        .from('knowledge_bases')
        .select('name')
        .eq('id', kbId)
        .single();
      
      const kbName = kb?.name;

      // Normalize the content for scientific terms
      const normalizedContent = normalizeScientificTerms(content);
      
      // Check for scientific terminology in the normalized query - we'll use an extremely low threshold
      const isScientificQuery = /anodonta|grandis|cadmium|cd|zn|cu|pb|hg|concentration|biomarker|metallothionein|mt|mussel|exposure|μg\/l|ug\/l|mg\/l/i.test(normalizedContent);
      
      console.log(`Query contains scientific terminology: ${isScientificQuery ? 'YES' : 'NO'}`);
      if (normalizedContent !== content) {
        console.log(`Normalized query: "${normalizedContent}"`);
      }
      
      // Use an extremely low threshold for scientific queries to ensure we find relevant documents
      // Scientific terminology often doesn't match well with embedding similarity
      const searchThreshold = isScientificQuery ? 0.01 : 0.20;
      
      // Use a higher limit for scientific queries to ensure we get more context
      const searchLimit = isScientificQuery ? 25 : 15;
      
      console.log(`Using search threshold: ${searchThreshold} and limit: ${searchLimit}`);
      
      const relevantDocs = await semanticSearch(kbId, normalizedContent, { 
        limit: searchLimit,
        threshold: searchThreshold,
        logDiagnostics: true
      });
      
      console.log(`Found ${relevantDocs.length} relevant documents with similarities:`, 
        relevantDocs.map(doc => ({
          name: doc.name,
          similarity: doc.similarity ? (doc.similarity * 100).toFixed(2) + '%' : 'unknown'
        }))
      );
      
      if (relevantDocs.length > 0) {
        await addSystemMessage(kbName ? 
          `🔍 Searching knowledge base "${kbName}" for relevant information...` :
          `🔍 Searching knowledge base for relevant information...`
        );
        
        let contextContent = '';
        
        // For scientific queries, include ALL content from each document with minimal truncation
        const maxContextLength = isScientificQuery ? 8000 : 4000;
        
        relevantDocs.forEach((doc, index) => {
          contextContent += `Document ${index + 1}: ${doc.name}\n`;
          
          if ('similarity' in doc) {
            contextContent += `Similarity: ${(doc.similarity! * 100).toFixed(2)}%\n`;
          }
          
          const truncated = doc.content.length > maxContextLength;
          contextContent += `${doc.content.substring(0, maxContextLength)}${truncated ? '...' : ''}\n\n`;
        });
        
        return {
          found: true,
          empty: false,
          contextContent,
          kbName
        };
      }
      
      // Check if KB is empty or just has no relevant documents
      const { data: embeddingCheck, error: embeddingError } = await supabase
        .from('knowledge_base_documents')
        .select('id')
        .eq('knowledge_base_id', kbId)
        .limit(1);
        
      if (embeddingError) {
        console.error("Error checking knowledge base:", embeddingError);
        throw new Error(embeddingError.message);
      } else if (!embeddingCheck || embeddingCheck.length === 0) {
        console.warn("Knowledge base appears to be empty");
        await addSystemMessage(`⚠️ The knowledge base ${kbName ? `"${kbName}" ` : ''}appears to be empty. Please add documents to this knowledge base for it to be useful.`);
        
        return {
          found: true,
          empty: true,
          message: `The knowledge base with ID: ${kbId} appears to be empty. Please inform the user that they need to add documents to this knowledge base for it to be useful.`,
          kbName
        };
      } else {
        console.warn("Knowledge base has documents but none matched the query");
        await addSystemMessage(`⚠️ No relevant documents found in the knowledge base ${kbName ? `"${kbName}" ` : ''}for your query. Try a different query or check if the knowledge base has relevant content.`);
        
        // For scientific queries about Anodonta grandis, provide more helpful guidance
        if (/anodonta\s+grandis(\s+grandis)?/i.test(content)) {
          return {
            found: true,
            empty: false,
            noMatches: true,
            message: `You are an assistant with access to a knowledge base. I searched the knowledge base for information about "${content}" but couldn't find specific information about Anodonta grandis grandis.
              
              I can tell you that Anodonta grandis is a species of freshwater mussel native to North America. However, the knowledge base doesn't contain specific research data about this species.
              
              The knowledge base may contain information about:
              1. Other freshwater mussels and their responses to heavy metals
              2. General information about metallothionein induction in aquatic organisms
              3. Data on cadmium exposure and bioaccumulation in similar species
              
              If you'd like, I can search for more general information about freshwater mussels and heavy metal exposure instead. Or I can provide general information about Anodonta grandis from my training.`,
            kbName
          };
        } else if (isScientificQuery) {
          return {
            found: true,
            empty: false,
            noMatches: true,
            message: `You are an assistant with access to a knowledge base. I searched the knowledge base for information about "${content}" but couldn't find exact matches. 
              
              The knowledge base may contain information about similar scientific topics but my search couldn't retrieve it effectively. 
              
              Here are some suggestions:
              1. Try asking about more general topics like "mussels and heavy metals"
              2. Try asking specifically about "metallothionein induction in mussels"
              3. Ask about "cadmium bioaccumulation in freshwater mussels"
              
              I'll do my best to answer based on general knowledge, but without specific data from the knowledge base.`,
            kbName
          };
        } else {
          return {
            found: true,
            empty: false,
            noMatches: true,
            message: `You are an assistant with access to a knowledge base. I couldn't find any documents in the knowledge base that match the query about "${content}". 
              The knowledge base has documents but none of them seem relevant to your question.`,
            kbName
          };
        }
      }
    } catch (error) {
      console.error("Error performing semantic search:", error);
      
      await addSystemMessage(`⚠️ Error searching the knowledge base: ${error instanceof Error ? error.message : 'Unknown error'}`);
      
      return {
        found: false,
        error: true,
        message: `You are an assistant. There was an error searching the knowledge base with ID: ${kbId}. 
          Please apologize to the user and suggest they try again or contact support if the issue persists.
          The specific error was: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  };

  return {
    findKnowledgeBaseInContent,
    performKnowledgeBaseSearch
  };
}
